import React, {
    FunctionComponent,
    useContext,
    createContext,
    useState,
    useEffect,
    useReducer,
} from 'react';
import {
    AIChatbotConfigs,
    getAIChatbotConfigs,
    updateAIChatbotConfigs,
} from '../../../api-client/AIChatbotService';
import { UiStateDef } from '../../../types/ui-state';
import { useStore, StoreProviderValue } from '../../../store/storeContext';
import { aiChatbotStoreKey } from './AIChatbotConstant';
import { aiChatbotReducer } from './AIChatbotReducer';
import {
    AIChatbotStateDef,
    UPDATE_STATE,
    UPDATE_TOGGLE,
    UPDATE_WELCOME_MESSAGE,
    UPDATE_CURSOR_CONTEXT_PLUGIN,
} from './AIChatbotTypes';
import {
    NotificationProviderValue,
    useNotification,
} from '../../Notification/NotificationBarContext';
import { StoreStateDef } from '../../../store/storeInitialState';

export interface AIChatbotProviderValue {
    uiState: UiStateDef;
    updateUiState: (newState: UiStateDef) => void;
    aiChatbotConfigs: AIChatbotConfigs;
    setAIChatbotConfigs: React.Dispatch<React.SetStateAction<AIChatbotConfigs>>;
    aiChatbotState: AIChatbotStateDef;
    updateState: (updatedState: AIChatbotStateDef) => void;
    storeState: StoreStateDef;
    updateToggleState: (updatedState: boolean) => void;
    updateWelcomeMessageState: (updatedState: string) => void;
    saveData: () => void;
    updateContextPluginToggleState: (updatedState: boolean) => void;
}

export type AIChatbotContextValue = AIChatbotProviderValue | undefined;

const AIChatbotContext = createContext<AIChatbotContextValue>(undefined);

const AIChatbotProvider: FunctionComponent = ({ children }) => {
    const aiChatbotInitialState: AIChatbotStateDef = {
        isAllowedInSubscription: false,
        isAiEnabled: false,
        welcomeMessage: '',
        contextPlugins: {
            showMarketingContent: false,
            isAllowedInSubscription: false,
            cursorIntegration: { isEnabled: false },
        },
    };
    const [aiChatbotState, aiChatbotDispatch] = useReducer(aiChatbotReducer, aiChatbotInitialState);
    const { updateStoreApiGroup, storeState } = useStore() as StoreProviderValue;
    const { addNotification } = useNotification() as NotificationProviderValue;

    const [uiState, updateUiState] = useState<UiStateDef>({
        isLoading: true,
        isError: false,
        isSaving: false,
        statusCode: 0,
        message: '',
    });

    const updateState = (updatedState: AIChatbotStateDef): void => {
        aiChatbotDispatch({
            type: UPDATE_STATE,
            payload: updatedState,
        });
    };

    const updateToggleState = (updatedState: boolean): void => {
        aiChatbotDispatch({
            type: UPDATE_TOGGLE,
            payload: updatedState,
        });
    };

    const updateContextPluginToggleState = (updatedState: boolean): void => {
        return aiChatbotDispatch({
            type: UPDATE_CURSOR_CONTEXT_PLUGIN,
            payload: updatedState,
        });
    };

    const updateWelcomeMessageState = (updatedState: string): void => {
        aiChatbotDispatch({
            type: UPDATE_WELCOME_MESSAGE,
            payload: updatedState,
        });
    };

    useEffect(() => {
        const cacheData = storeState.apiGroup.aiChatbot;

        if (cacheData) {
            updateUiState({
                ...uiState,
                isLoading: false,
            });
            return updateState(cacheData);
        } else {
            updateUiState({
                ...uiState,
                isLoading: true,
            });
        }

        getAIChatbotConfigs()
            .then((value) => {
                updateState(value);
                updateStoreApiGroup(value, aiChatbotStoreKey);
                updateUiState({
                    ...uiState,
                    isLoading: false,
                });
                if (value.additionalInformation?.email) {
                    const settings = JSON.parse(localStorage.getItem('portalEditorSettings') || '{}');
                    localStorage.setItem(
                        'portalEditorSettings',
                        JSON.stringify({ ...settings, userEmail: value.additionalInformation.email })
                    );
                }
            })
            .catch(() => {
                updateStoreApiGroup(aiChatbotState, aiChatbotStoreKey);
                updateUiState({ ...uiState, isLoading: false, isError: true });
            });
    }, []);

    const saveData = () => {
        updateUiState({
            ...uiState,
            isSaving: true,
        });
        updateAIChatbotConfigs({
            isEnabled: aiChatbotState.isAiEnabled,
            welcomeMessage: aiChatbotState.welcomeMessage,
            contextPlugins: aiChatbotState.contextPlugins,
        })
            .then(() => {
                updateUiState({
                    ...uiState,
                    isSaving: false,
                });
                addNotification({
                    show: true,
                    type: 'success',
                    message: 'Page was saved successfully.',
                });
                const data = {
                    isAllowedInSubscription: aiChatbotState.isAllowedInSubscription,
                    isAiEnabled: aiChatbotState.isAiEnabled,
                    welcomeMessage: aiChatbotState.welcomeMessage,
                    contextPlugins: aiChatbotState.contextPlugins,
                };
                updateState(data);
                updateStoreApiGroup({ ...aiChatbotState, ...data }, aiChatbotStoreKey);
            })
            .catch(() => {
                updateUiState({
                    ...uiState,
                    isSaving: false,
                });
                addNotification({
                    show: true,
                    type: 'error',
                    message: 'Page was not saved successfully.',
                });
            });
    };

    const value = {
        aiChatbotState,
        uiState,
        updateUiState,
        updateState,
        storeState,
        updateToggleState,
        updateWelcomeMessageState,
        saveData,
        updateContextPluginToggleState,
    };

    return <AIChatbotContext.Provider value={value}>{children}</AIChatbotContext.Provider>;
};

const useAIChatbot = () => useContext(AIChatbotContext);

export { AIChatbotProvider, useAIChatbot };
